import { FunctionComponent, ReactNode } from 'react'
import { css } from '@emotion/css'
import { Theme, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import Map from '@/components/Map'
import type { Venue } from '@/libs/helpers/adapters'
import { buildAssetsPath } from '@/libs/helpers/assets'
import { formatPhone } from '@/libs/helpers/formatters'
import { ConsumerAddress, DeliveryFleet, OrderMethod } from '@/libs/helpers/utils'
import { useConfiguration } from '@/providers/configuration/ConfigurationContext'
import type { OrderingAddress } from '@/providers/googleMaps'
import { Button, CheckBox, Grid, SvgIcon, Text } from '@/tbui'
import PulsateLoader from './PulsateLoader'

interface LocationDetailsProps {
  openOrderMethodSelector: () => void
  venue?: Venue
  orderingAddress?: OrderingAddress
  deliveryFleet?: DeliveryFleet
  saveDeliveryAddress?: boolean
  onSaveDeliveryAddressChanged?: () => void
  savedAddresses?: ConsumerAddress[]
  isCartLoading?: boolean
  consumerPhoneNumber?: string
  orderMethod: OrderMethod
}

const StyledLogo = styled.img`
  height: auto;
  width: 110px;
  margin-left: 5px;
`

const StyledSavedAddressText = styled(Text)`
  line-height: 20px;
`

const StyledSaveAddressText = styled(Text)`
  line-height: 24px;
`

const detailsStyles = (theme: Theme): string => css`
  @media (max-width: ${theme.breakpoints.SM}) {
    margin-bottom: 1rem;
  }
`

const LocationDetails: FunctionComponent<LocationDetailsProps> = ({
  openOrderMethodSelector,
  venue,
  orderingAddress,
  deliveryFleet,
  saveDeliveryAddress = false,
  onSaveDeliveryAddressChanged,
  savedAddresses = [],
  isCartLoading,
  consumerPhoneNumber,
  orderMethod,
}) => {
  const theme = useTheme()
  const { userSession } = useConfiguration()
  const hasDDIntegration = deliveryFleet === DeliveryFleet.DOORDASH
  const { t } = useTranslation()

  const isDelivery = orderMethod === OrderMethod.delivery
  const showEditOrderMethodDetails = Boolean(isDelivery && orderingAddress)

  if (
    !userSession.isInitialized ||
    !orderMethod ||
    !venue ||
    (isDelivery && orderingAddress == null)
  ) {
    return null
  }

  const isAddressSaved =
    isDelivery &&
    orderingAddress != null &&
    savedAddresses.some(
      (savedAddress) =>
        savedAddress.googlePlaceID === orderingAddress.googlePlaceID &&
        savedAddress.aptAndSuiteNumber === orderingAddress.address2
    )

  const renderMap = ({
    longitude,
    latitude,
    address,
    phone,
  }: {
    longitude?: number | null
    latitude?: number | null
    address?: string
    phone?: string
  }): ReactNode => (
    <Grid template="auto 1fr" gap="1rem">
      {!isCartLoading && latitude != null && longitude != null ? (
        <Map latitude={latitude} longitude={longitude} />
      ) : (
        <PulsateLoader height="150px" width="170px" />
      )}
      <Grid align="space-between" gap="10px">
        <Text type="H4">
          {t('checkout.location.title', { context: orderMethod.toLowerCase() })}
        </Text>
        <Text>{address}</Text>
        {phone && <Text>{formatPhone(phone)}</Text>}
        {isDelivery &&
          (isAddressSaved ? (
            <Grid
              template="auto 1fr auto"
              gap="0.5rem"
              align="flex-start"
              data-test="location-details-address-saved"
            >
              <SvgIcon name="star_filled" fontSize="20px" color="GRAY_4" />
              <StyledSavedAddressText>
                {t('order_method_selector.saved_address')}
              </StyledSavedAddressText>
            </Grid>
          ) : (
            userSession.isSignedIn && (
              <Grid
                template="auto 1fr auto"
                gap="0.5rem"
                align="flex-start"
                data-test="location-details-save-address"
              >
                <CheckBox
                  iconSize="24px"
                  id="save-delivery-address-checkout"
                  isSelected={saveDeliveryAddress}
                  onChange={onSaveDeliveryAddressChanged}
                  data-test="location-details-save-address-checkbox"
                  variant="SQUARE"
                />
                <StyledSaveAddressText>
                  {t('order_method_selector.save_address')}
                </StyledSaveAddressText>
              </Grid>
            )
          ))}
      </Grid>
    </Grid>
  )

  return (
    <Grid data-test="location-details" gap={showEditOrderMethodDetails ? '1.75rem' : 0}>
      <Grid xs="auto" sm="1fr auto" align="center">
        <Text type="H3" className={detailsStyles(theme)}>
          {t('checkout.subheader.title', { context: orderMethod.toLowerCase() })}
        </Text>
        {showEditOrderMethodDetails && (
          <Button
            variant="OUTLINE"
            onClick={openOrderMethodSelector}
            type="button"
            data-test="edit-location"
            center
          >
            <div css={{ display: 'flex', alignItems: 'center' }}>
              <SvgIcon
                name="edit"
                fontSize="21px"
                color="PRIMARY"
                style={{ marginRight: '0.5rem' }}
              />
              {t('order_method_selector.edit')}
            </div>
          </Button>
        )}
      </Grid>
      {isDelivery && hasDDIntegration && (
        <Text>
          {t('checkout.powered_by_doordash')}
          <StyledLogo alt="Doordash" src={buildAssetsPath('/images/doordash_logo.png')} />
        </Text>
      )}
      {renderMap(
        isDelivery
          ? {
              address: orderingAddress?.formattedAddress,
              longitude: orderingAddress?.lng,
              latitude: orderingAddress?.lat,
              phone: consumerPhoneNumber,
            }
          : {
              longitude: venue.longitude,
              latitude: venue.latitude,
              address: venue.address,
              phone: venue.phone,
            }
      )}
    </Grid>
  )
}

export default LocationDetails
